import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { AlertTriangle, X, ArrowRight } from "lucide-react";

// Human-friendly copy per feature.
const FEATURE_COPY = {
  webinars_per_month: {
    label: "webinar",
    unit: "this month",
    icon: "webinars_this_month",
  },
  ai_generations_per_month: {
    label: "AI generation",
    unit: "this month",
    icon: "ai_generations_this_month",
  },
  landing_pages_total: {
    label: "landing page",
    unit: "on your workspace",
    icon: "landing_pages_total",
  },
  custom_domains_total: {
    label: "custom domain",
    unit: "connected",
    icon: "custom_domains_total",
  },
  team_seats_total: {
    label: "team member",
    unit: "invited",
    icon: "team_seats_total",
  },
};

const DISMISS_KEY_PREFIX = "vp:soft-limit-dismiss:";

/**
 * Shows amber banners for any feature currently at 80-99% usage.
 * Dismissable per-feature-per-month (localStorage). Poll happens once on mount
 * plus a lightweight refresh every 90 seconds so the banner appears mid-session
 * (right after the user's 8th AI generation, for example).
 */
export default function UsageWarnings() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [warnings, setWarnings] = useState([]);
  const [dismissed, setDismissed] = useState(() => {
    try {
      const raw = localStorage.getItem(DISMISS_KEY_PREFIX + "set");
      return new Set(raw ? JSON.parse(raw) : []);
    } catch { return new Set(); }
  });

  // Skip while impersonating or logged out
  const skip = !user || user.impersonating;

  useEffect(() => {
    if (skip) { setWarnings([]); return; }

    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/plans/limits");
        if (cancelled) return;
        // Skip if lifetime — they shouldn't see upgrade prompts
        if (data.lifetime) { setWarnings([]); return; }
        const w = [];
        for (const [limitKey, meta] of Object.entries(FEATURE_COPY)) {
          const limit = data.limits[limitKey];
          if (limit === null || limit === undefined || limit === false) continue; // unlimited / disabled
          const current = data.usage[meta.icon] || 0;
          const pct = current / limit;
          if (pct >= 0.8 && pct < 1) {
            const remaining = Math.max(0, limit - current);
            w.push({
              key: `${limitKey}:${data.usage.month || ""}`,
              feature: limitKey,
              current, limit, remaining,
              label: meta.label,
              unit: meta.unit,
              plan_label: data.label,
            });
          }
        }
        setWarnings(w);
      } catch { /* silent — non-critical */ }
    };
    load();
    const t = setInterval(load, 90000);
    return () => { cancelled = true; clearInterval(t); };
  }, [skip, user?.email]);

  const dismiss = (k) => {
    const next = new Set(dismissed);
    next.add(k);
    setDismissed(next);
    try { localStorage.setItem(DISMISS_KEY_PREFIX + "set", JSON.stringify([...next])); } catch {}
  };

  const visible = warnings.filter((w) => !dismissed.has(w.key));
  if (visible.length === 0) return null;

  // Only render the "most urgent" banner at a time so we don't stack UI clutter.
  const w = visible.sort((a, b) => (a.remaining - b.remaining))[0];

  return (
    <div
      data-testid="soft-limit-banner"
      className="mx-4 sm:mx-6 lg:mx-8 mt-4 rounded-xl border border-[#F59E0B]/40 bg-[#FFFBEB] px-4 py-3 flex items-center justify-between gap-3 flex-wrap"
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-9 h-9 rounded-lg bg-[#F59E0B]/15 flex items-center justify-center flex-shrink-0">
          <AlertTriangle className="w-4 h-4 text-[#B45309]" strokeWidth={2} />
        </div>
        <div className="min-w-0 text-sm">
          <span className="font-semibold text-[#B45309]">
            {w.remaining} {w.label}{w.remaining === 1 ? "" : "s"} left {w.unit}.
          </span>{" "}
          <span className="text-[#78350F]">
            You've used {w.current} of your {w.plan_label} plan's {w.limit}. Upgrade for unlimited.
          </span>
        </div>
      </div>
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <button
          onClick={() => nav("/app/plans")}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#B45309] text-white hover:bg-[#92400E] text-xs sm:text-sm font-semibold"
          data-testid="soft-limit-upgrade-btn"
        >
          Upgrade <ArrowRight className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={() => dismiss(w.key)}
          className="p-1.5 text-[#B45309] hover:bg-[#F59E0B]/15 rounded-md"
          aria-label="Dismiss"
          data-testid="soft-limit-dismiss-btn"
        >
          <X className="w-4 h-4" strokeWidth={2} />
        </button>
      </div>
    </div>
  );
}
